import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { WorkspaceProvider } from '@/contexts/WorkspaceContext';
import { ProtectedRoute, PublicRoute, PostLoginLanding } from '@/components/ProtectedRoute';
import WorkspaceGuard from '@/components/WorkspaceGuard';
import AppLayout from '@/components/AppLayout';
import LoginPage from '@/pages/LoginPage';
import SignupPage from '@/pages/SignupPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import InvitationAcceptPage from '@/pages/InvitationAcceptPage';
import OnboardingPage from '@/pages/OnboardingPage';
import EmployerOnboardingPage from '@/pages/EmployerOnboardingPage';
import DashboardPage from '@/pages/DashboardPage';
import JobsPage from '@/pages/JobsPage';
import JobDetailPage from '@/pages/JobDetailPage';
import CvLibraryPage from '@/pages/CvLibraryPage';
import ApplicationsPage from '@/pages/ApplicationsPage';
import ApplicationPackDetailPage from '@/pages/ApplicationPackDetailPage';
// RC 1.49.7 Phase 0 — Application Workspace (route-based replacement
// for the modal wizard). Mounted unconditionally; the JobDetailPage
// Apply button decides whether to navigate here based on
// `isWorkspaceModeEnabled()` so the route exists for direct deep-
// links + manual QA even while the flag is OFF.
import ApplicationWorkspacePage from '@/pages/ApplicationWorkspacePage';
import AutomationPage from '@/pages/AutomationPage';
import AdminSourcesPage from '@/pages/AdminSourcesPage';
import AdminCataloguePage from '@/pages/AdminCataloguePage';
import AdminJobsInventoryPage from '@/pages/AdminJobsInventoryPage';
import AdminDuplicateClustersPage from '@/pages/AdminDuplicateClustersPage';
import AdminSettingsPage from '@/pages/AdminSettingsPage';
import AdminUsersPage from '@/pages/AdminUsersPage';
import AdminBillingPage from '@/pages/AdminBillingPage';
import AdminAnalyticsPage from '@/pages/AdminAnalyticsPage';
import AdminScoringPage from '@/pages/AdminScoringPage';
import AdminRetentionPage from '@/pages/AdminRetentionPage';
import AdminEmployersPage from '@/pages/AdminEmployersPage';
import EmployerPermissionsPage from '@/pages/EmployerPermissionsPage';
import EmployerWorkflowPage from '@/pages/EmployerWorkflowPage';
import EmployerAccountPage from '@/pages/EmployerAccountPage';
import EmployerCommandCentrePage from '@/pages/EmployerCommandCentrePage';
import EmployerOrganisationPage from '@/pages/EmployerOrganisationPage';
import EmployerCandidatesPage from '@/pages/EmployerCandidatesPage';
import EmployerSignalsPage from '@/pages/EmployerSignalsPage';
import EmployerHomePage from '@/pages/EmployerHomePage';
import EmployerMembersPage from '@/pages/EmployerMembersPage';
import EmployerReviewsPage from '@/pages/EmployerReviewsPage';
import EmployerReviewerDossierPage from '@/pages/EmployerReviewerDossierPage';
import EmployerReviewersAdminPage from '@/pages/EmployerReviewersAdminPage';
import EmployerRolesPage from '@/pages/EmployerRolesPage';
import EmployerRoleDetailPage from '@/pages/EmployerRoleDetailPage';
import EmployerRoleEditorPage from '@/pages/EmployerRoleEditorPage';
import EmployerRoleWizardPage from '@/pages/EmployerRoleWizardPage';
import EmployerRoleMatchesPage from '@/pages/EmployerRoleMatchesPage';
import CandidateSignalsPage from '@/pages/CandidateSignalsPage';
import OperatorOnlyRoute from '@/components/OperatorOnlyRoute';
import ChooseWorkspacePage from '@/pages/ChooseWorkspacePage';
import NoAccessPage from '@/pages/NoAccessPage';
// RC 1.41 Phase C — generation pages are no longer linked from any
// nav. The /cv-variants and /cover-letters URLs are kept alive
// strictly as deep-link redirects to /documents (`LegacyDocsRedirect`
// below). The standalone generator components are intentionally not
// imported anywhere in this file; Phase E will retire the source
// files once all callers are gone.
import CreditsPage from '@/pages/CreditsPage';
import SettingsPage from '@/pages/SettingsPage';
import PrivacyPage from '@/pages/PrivacyPage';
import TermsPage from '@/pages/legal/TermsPage';
import RefundPage from '@/pages/legal/RefundPage';
import PrivacyPolicyPage from '@/pages/legal/PrivacyPolicyPage';
import ProofPreviewPage from '@/pages/ProofPreviewPage';
import DocumentsPage from '@/pages/DocumentsPage';
import AccountPage from '@/pages/AccountPage';
import HowItWorksPage from '@/pages/HowItWorksPage';
import HelpGuidePage from '@/pages/HelpGuidePage';
import OnboardingPlanPage from '@/pages/OnboardingPlanPage';
import OnboardingPaymentPage from '@/pages/OnboardingPaymentPage';
import PlanActivationHandoff from '@/pages/PlanActivationHandoff';
import EntitlementGate from '@/components/EntitlementGate';
import { Toaster } from '@/components/ui/sonner';
import '@/App.css';

/**
 * RC 1.38.A.UI.FLOW — `<LegacyDocsRedirect tab="..." />`
 *
 * Replaces a bare `<Navigate to="/documents?tab=..." replace />`. Preserves
 * any incoming query params (e.g. `?job=<id>`) by merging them with the
 * `tab` param so deep-links from `NextStepStrip` survive the redirect and
 * the embedded CV-Variants / Cover-Letter pages can prefill from the job
 * context.
 */
function LegacyDocsRedirect({ tab }) {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.set('tab', tab);
  return <Navigate to={`/documents?${params.toString()}`} replace />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <WorkspaceProvider>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />

            {/* RC 1.35.F2 Stage-1 P0-5 — public password-reset flow.
                Both routes are public: forgot-password is reachable
                from the login form; /auth/reset is the Supabase
                recovery-link landing page. */}
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/auth/reset"      element={<ResetPasswordPage />} />

            {/* RC 1.63 — In-app user guides (public, screenshot-driven).
                Mirrors the markdown files in /public/help so PDFs can
                be exported via a print-friendly ?print=1 query flag. */}
            <Route path="/help/:guide" element={<HelpGuidePage />} />

            {/* Phase 0.7.1 — Invitation acceptance landing page.
                Mounted OUTSIDE PublicRoute + ProtectedRoute so it
                renders for both logged-out and logged-in users; the
                page handles auth state internally and surfaces the
                sign-in CTA with a returnTo back to itself. */}
            <Route path="/invitations/accept" element={<InvitationAcceptPage />} />

            {/* RC 1.35.F1 — Public legal pages (no auth gate so Stripe
                live-mode review can load them directly). */}
            <Route path="/terms"   element={<TermsPage />} />
            <Route path="/refund"  element={<RefundPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />

            {/* Internal operator preview for the homepage Proof
                Section artifacts (synthetic CV, paraphrased JD,
                NDOR-tailored CV, cover letter). Public so the
                operator can review on any device without auth. */}
            <Route path="/proof" element={<ProofPreviewPage />} />

            {/* Onboarding - auth required.
                RC 1.44.O1.A.2 (2026-04-30) — `redirectIfProfile` removed.
                Rationale: the route-level `<Navigate>` runs during render
                and synchronously beats the OnboardingPage's
                `setCompleted → navigate('/onboarding/plan')` useEffect,
                hijacking brand-new users to /post-login → /dashboard
                immediately after `refreshProfile()` flips `hasProfile`.
                OnboardingPage already self-handles the
                returning-bookmark case via its
                `hasProfile && !cvData && !saving → /dashboard` shortcut,
                so the route guard is redundant AND the actual
                race-trigger. */}
            <Route path="/onboarding" element={
              <ProtectedRoute requireProfile={false}>
                <OnboardingPage />
              </ProtectedRoute>
            } />

            {/* Employer onboarding — used when the signup role is
                'employer'. Lives OUTSIDE the AppLayout + EntitlementGate
                shell (per product: "EntitlementGate must not interrupt
                role/workspace onboarding"). */}
            <Route path="/onboarding/employer" element={
              <ProtectedRoute requireProfile={false} redirectIfProfile={true}>
                <EmployerOnboardingPage />
              </ProtectedRoute>
            } />

            {/* RC 1.32 — Plan selection + stub payment. requireProfile
                stays true so the usual auth flow runs, but the
                EntitlementGate is intentionally NOT applied here —
                these routes ARE the gate's escape hatch. */}
            <Route path="/onboarding/plan" element={
              <ProtectedRoute requireProfile={true}>
                <OnboardingPlanPage />
              </ProtectedRoute>
            } />
            <Route path="/onboarding/payment" element={
              <ProtectedRoute requireProfile={true}>
                <OnboardingPaymentPage />
              </ProtectedRoute>
            } />

            {/* RC 1.44.O1.B — Forced re-auth handoff after plan
                activation. Sits OUTSIDE the EntitlementGate (same
                escape-hatch rule as /onboarding/plan + /payment)
                because the entitlement is fresh at this point but
                the gate should not interrupt a single-action trust
                screen. Both the Base activation (OnboardingPlanPage)
                and the Paid activation (OnboardingPaymentPage)
                redirect here on success; the CTA forces signOut() +
                routes to /login?just_signed_up=1 for a clean session
                rehydration. */}
            <Route path="/plan-activated" element={
              <ProtectedRoute requireProfile={true}>
                <PlanActivationHandoff />
              </ProtectedRoute>
            } />

            {/* Protected routes with app shell */}
            <Route element={
              <ProtectedRoute requireProfile={true}>
                <EntitlementGate>
                  <AppLayout />
                </EntitlementGate>
              </ProtectedRoute>
            }>
              {/* Workspace-chooser + zero-access + post-login screens.
                  These do NOT belong to a workspace — they're routed
                  first so their logic beats the guards below. */}
              <Route path="/post-login"       element={<PostLoginLanding />} />
              <Route path="/choose-workspace" element={<ChooseWorkspacePage />} />
              <Route path="/no-access"        element={<NoAccessPage />} />

              {/* Candidate workspace (existing surfaces — unchanged). */}
              <Route element={<WorkspaceGuard workspace="candidate"><Outlet /></WorkspaceGuard>}>
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/jobs" element={<JobsPage />} />
                <Route path="/jobs/:id" element={<JobDetailPage />} />
                {/* Legacy routes — kept as permanent redirects to /documents and
                    /account so deep links keep working. RC 1.20.
                    RC 1.38.A.UI.FLOW — preserve query params (e.g. ?job=)
                    so prefill survives the redirect from NextStepStrip. */}
                <Route path="/cv-library"    element={<LegacyDocsRedirect tab="uploaded" />} />
                <Route path="/cv-variants"   element={<LegacyDocsRedirect tab="variants" />} />
                <Route path="/cover-letters" element={<LegacyDocsRedirect tab="letters"  />} />
                <Route path="/analyze-fit"   element={<Navigate to="/jobs" replace />} />
                {/* RC 1.35.F3 P1.17 — /credits is now a dedicated read-only
                    billing dashboard (Plan + Top-up Credits, recent
                    transactions, Customer Portal CTA, Buy more CTA).
                    Replaces the prior 1.33.B redirect to /account?tab=plan. */}
                <Route path="/credits"  element={<CreditsPage />} />
                <Route path="/settings" element={<Navigate to="/account?tab=settings" replace />} />
                <Route path="/documents" element={<DocumentsPage />} />
                <Route path="/account"   element={<AccountPage />} />
                <Route path="/applications" element={<ApplicationsPage />} />
                <Route path="/applications/:id" element={<ApplicationPackDetailPage />} />
                {/* RC 1.49.7 Phase 0 — Application Workspace (flag-
                    gated by REACT_APP_WORKSPACE_MODE_ENABLED in
                    JobDetailPage's Apply handler). Route is always
                    registered so direct deep-links and operator QA
                    work regardless of the flag. */}
                <Route
                  path="/applications/workspace/:jobId"
                  element={<ApplicationWorkspacePage />}
                />
                <Route path="/automation" element={<AutomationPage />} />
                {/* RC 1.49.7 (2026-05-09) — Operator-only Signals.
                    Hidden from non-operators while the Employers-side
                    outbound interest workflow is still pre-launch. The
                    sidebar entry is filtered for non-operators in
                    AppLayout; this guard is the matching back-stop so
                    a direct visit to /candidate/signals (deep-link or
                    bookmark) silently redirects non-operators to
                    /dashboard. The route stays mounted so the operator
                    can keep dogfooding without a flag flip. */}
                <Route
                  path="/candidate/signals"
                  element={
                    <OperatorOnlyRoute>
                      <CandidateSignalsPage />
                    </OperatorOnlyRoute>
                  }
                />
                <Route path="/how-it-works" element={<HowItWorksPage />} />
              </Route>

              {/* Employer workspace (RC 1.30.B0 stub + RC 1.30.B
                  organisation + RC 1.30.C roles). */}
              <Route element={<WorkspaceGuard workspace="employer"><Outlet /></WorkspaceGuard>}>
                <Route path="/employer"                element={<EmployerCommandCentrePage />} />
                <Route path="/employer/organisation"   element={<EmployerOrganisationPage />} />
                <Route path="/employer/candidates"     element={<EmployerCandidatesPage />} />
                <Route path="/employer/signals"        element={<EmployerSignalsPage />} />
                <Route path="/employer/account"        element={<EmployerAccountPage />} />
                {/* Phase 0.7 Slice S7/S8 — Member management (Invitations, Suspensions, Bulk, Audit).
                    Writes flow exclusively through the `/api/employer/v1/*` FastAPI shim;
                    reads use Supabase governed by S6 RLS. */}
                <Route path="/employer/members"        element={<EmployerMembersPage />} />
                <Route path="/employer/help/permissions" element={<EmployerPermissionsPage />} />
                <Route path="/employer/help/workflow"    element={<EmployerWorkflowPage />} />
                <Route path="/employer/reviews"        element={<EmployerReviewsPage />} />
                <Route path="/employer/reviews/:reviewId" element={<EmployerReviewerDossierPage />} />
                <Route path="/employer/reviewers"      element={<EmployerReviewersAdminPage />} />
                {/* Phase 0.6 Slice A — new pillar-grouped roles list + read-only detail. */}
                {/* Phase 0.6 Slice B — wizard rebind on /new + /edit; legacy editor kept on disk for rollback. */}
                <Route path="/employer/roles"          element={<EmployerRolesPage />} />
                <Route path="/employer/roles/new"      element={<EmployerRoleWizardPage />} />
                <Route path="/employer/roles/:id"      element={<EmployerRoleDetailPage />} />
                <Route path="/employer/roles/:id/edit" element={<EmployerRoleWizardPage />} />
                <Route
                  path="/employer/roles/:id/matches"
                  element={
                    /* MVP Channel-2 safety gate (Phase 1.1).
                       Channel-2 matching/signals is post-MVP per
                       MVP_Scope_Lock_v1_Employer.md Part 2. Until
                       the post-MVP path is re-opened, the matches
                       surface is operator-only-gated so it doesn't
                       leak as employer-ready functionality. */
                    <OperatorOnlyRoute>
                      <EmployerRoleMatchesPage />
                    </OperatorOnlyRoute>
                  }
                />
              </Route>

              {/* Admin workspace. require_admin server-side gates the
                  endpoints; the guard additionally hides the routes
                  for non-admins at the SPA layer. */}
              <Route element={<WorkspaceGuard workspace="admin"><Outlet /></WorkspaceGuard>}>
                <Route path="/admin/sources"   element={<AdminSourcesPage />} />
                <Route path="/admin/catalogue" element={<AdminCataloguePage />} />
                <Route path="/admin/jobs"      element={<AdminJobsInventoryPage />} />
                <Route path="/admin/duplicates" element={<AdminDuplicateClustersPage />} />
                <Route path="/admin/settings" element={<AdminSettingsPage />} />
                <Route path="/admin/users"    element={<AdminUsersPage />} />
                <Route path="/admin/billing"  element={<AdminBillingPage />} />
                <Route path="/admin/analytics" element={<AdminAnalyticsPage />} />
                <Route path="/admin/scoring"   element={<AdminScoringPage />} />
                <Route path="/admin/retention" element={<AdminRetentionPage />} />
                {/* Founder Console Pass 1 — admin-only employer
                    operational visibility. Operator-only beyond
                    is_admin so non-founder admins don't see other
                    tenants' audit rows. */}
                <Route
                  path="/admin/employers"
                  element={
                    <OperatorOnlyRoute>
                      <AdminEmployersPage />
                    </OperatorOnlyRoute>
                  }
                />
              </Route>
            </Route>

            {/* Catch-all — the redirect target is computed at runtime
                by PostLoginLanding via the workspace context. */}
            <Route path="*" element={<Navigate to="/post-login" replace />} />
          </Routes>
          <Toaster position="top-right" />
        </WorkspaceProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
